import type { WebContext } from './context';

import { installSandbox, purgePrivateData, stopSandbox } from './sandbox';

import './bootstrap.css';

const tabStorage = window.sessionStorage;
let csrf = '';
let holder = '';
let sessionId = '';
let ending = false;
let stopHeartbeat = false;
let releaseTab: (() => void) | undefined;
const panel = document.createElement('section');
panel.id = 'tgready-panel';
const bar = document.createElement('aside');
bar.id = 'tgready-controls';
bar.setAttribute('aria-label', '网页账号信息');
const handle = document.createElement('button');
handle.id = 'tgready-drag';
handle.type = 'button';
handle.textContent = 'TG 账号';
handle.setAttribute('aria-label', '拖动信息面板，方向键可移动');
const usageText = document.createElement('span');
usageText.id = 'tgready-usage';
usageText.textContent = '家宽未启用';
let usesResidential = false;
const endButton = document.createElement('button');
endButton.id = 'tgready-end';
endButton.textContent = '结束';
endButton.disabled = true;
const sourceLicense = document.createElement('link');
sourceLicense.rel = 'license';
sourceLicense.href = '/source/telegram-web-source.tar.gz';
document.head.append(sourceLicense);
const actions = document.createElement('div');
actions.className = 'tgready-actions';
actions.append(usageText, endButton);
bar.append(handle, actions);
document.body.append(bar, panel);

const layoutKey = 'tgready-panel-layout';
let position: { x: number; y: number } | undefined;
try {
  const saved = JSON.parse(tabStorage.getItem(layoutKey) || '{}');
  if (Number.isFinite(saved.x) && Number.isFinite(saved.y)) position = { x: saved.x, y: saved.y };
} catch { /* An invalid saved layout must not block login. */ }
function saveLayout() {
  try {
    tabStorage.setItem(layoutKey, JSON.stringify(position));
  } catch { /* Optional UI preference. */ }
}
function place(x?: number, y?: number) {
  const rect = bar.getBoundingClientRect();
  const maxX = Math.max(12, innerWidth - rect.width - 12);
  const maxY = Math.max(12, innerHeight - rect.height - 12);
  position = {
    x: Math.max(12, Math.min(x ?? position?.x ?? innerWidth - rect.width - 20, maxX)),
    y: Math.max(12, Math.min(y ?? position?.y ?? 80, maxY)),
  };
  bar.style.left = `${position.x}px`;
  bar.style.top = `${position.y}px`;
}
let dragging: { id: number; dx: number; dy: number } | undefined;
handle.onpointerdown = (event) => {
  if (event.button !== 0) return;
  const rect = bar.getBoundingClientRect();
  dragging = { id: event.pointerId, dx: event.clientX - rect.left, dy: event.clientY - rect.top };
  handle.setPointerCapture(event.pointerId);
  bar.classList.add('is-dragging');
  event.preventDefault();
};
handle.onpointermove = (event) => {
  if (!dragging || dragging.id !== event.pointerId) return;
  place(event.clientX - dragging.dx, event.clientY - dragging.dy);
};
function finishDrag() {
  dragging = undefined;
  bar.classList.remove('is-dragging');
  saveLayout();
}
handle.onpointerup = finishDrag;
handle.onpointercancel = finishDrag;
handle.onlostpointercapture = finishDrag;
handle.onkeydown = (event) => {
  const delta: Record<string, [number, number]> = {
    ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1],
  };
  const direction = delta[event.key];
  if (!direction) return;
  event.preventDefault();
  event.stopPropagation();
  const step = event.shiftKey ? 32 : 12;
  place((position?.x || 0) + direction[0] * step, (position?.y || 0) + direction[1] * step);
  saveLayout();
};
window.addEventListener('resize', () => {
  place();
  saveLayout();
});
new ResizeObserver(() => place()).observe(bar);
place();

function show(heading: string, message: string, action?: { label: string; run: () => void }) {
  panel.hidden = false;
  panel.replaceChildren();
  const card = document.createElement('div');
  card.className = 'tgready-card';
  const h = document.createElement('h1');
  h.textContent = heading;
  const p = document.createElement('p');
  p.textContent = message;
  card.append(h, p);
  if (action) {
    const button = document.createElement('button');
    button.className = 'tgready-button';
    button.textContent = action.label;
    button.onclick = action.run;
    card.append(button);
  }
  panel.append(card);
  return card;
}

async function call(path: string, body?: unknown, method = 'POST') {
  const response = await fetch('/api/' + path, {
    method,
    credentials: 'same-origin',
    signal: AbortSignal.timeout(15000),
    headers: { 'content-type': 'application/json', 'x-csrf-token': csrf, 'x-web-holder': holder },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    const value = await response.json().catch(() => ({}));
    throw Object.assign(
      new Error(typeof value.detail === 'string' ? value.detail : '连接暂不可用，请重试'),
      { status: response.status },
    );
  }
  return response.json();
}

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
async function bindTab(id: string) {
  if (!navigator.locks) throw new Error('当前浏览器不支持独立标签锁，请使用新版 Chrome');
  let decide!: (value: boolean) => void;
  const ready = new Promise<boolean>((resolve) => {
    decide = resolve;
  });
  void navigator.locks.request('tgready-web:' + id, { ifAvailable: true }, async (lock) => {
    decide(Boolean(lock));
    if (lock) {
      await new Promise<void>((resolve) => {
        releaseTab = resolve;
      });
    }
  });
  if (!(await ready)) throw new Error('这个账号已在其他标签打开，请回到原标签操作');
}

async function closeSession() {
  if (!sessionId || ending) return;
  ending = true;
  stopSandbox();
  show('正在结束网页操作', '正在关闭连接并回收本次网页授权。确认结束后，同号自动任务会继续。');
  try {
    const row = await call(`telegram-web/sessions/${sessionId}/end`);
    applyStatus(row);
  } catch (error) {
    show('正在等待回收确认', (error as Error).message);
    ending = false;
  }
}
endButton.onclick = () => {
  void closeSession();
};

function applyStatus(row: any) {
  const labels: Record<string, string> = {
    waiting_account: '等待账号空闲',
    authorizing: '正在授权',
    waiting_verification: '等待 Telegram 验证',
    active: '已连接',
    reconnecting: '正在重连',
    revoking: '正在回收授权',
    needs_review: '授权结果待核对',
    closed: '已结束',
  };
  if (row.state === 'closed') {
    stopHeartbeat = true;
    stopSandbox();
    endButton.disabled = true;
    releaseTab?.();
    tabStorage.removeItem('tgready-session');
    tabStorage.setItem('tgready-cleanup', '1');
    location.replace('/ended');
    show('网页操作已结束', '本次网页授权已回收，同号自动任务可以继续执行。', {
      label: '重新打开',
      run: () => location.reload(),
    });
  } else if (row.state === 'revoking' || row.state === 'needs_review') {
    stopSandbox();
    show(labels[row.state], row.reason || '正在核对本次网页授权，其他账号不受影响。');
  } else if (row.state === 'waiting_account') {
    show('等待这个账号空闲', row.reason || '等待当前任务正常结束，其他账号照常执行。');
  }
}

async function heartbeat() {
  while (!stopHeartbeat) {
    await sleep(15000);
    if (stopHeartbeat) return;
    try {
      applyStatus(await call(`telegram-web/sessions/${sessionId}/heartbeat`));
      if (usesResidential) {
        const usage = await call(`telegram-web/sessions/${sessionId}/usage`, undefined, 'GET');
        const bytes = Number(usage.upload_bytes) + Number(usage.download_bytes);
        usageText.textContent = `家宽 ${(bytes / 1e9).toFixed(4)} GB`;
      }
    } catch (error) {
      show('网页连接已暂停', (error as Error).message, {
        label: '重新连接',
        run: () => location.reload(),
      });
      stopSandbox();
      stopHeartbeat = true;
    }
  }
}

export async function bootstrap() {
  if (location.pathname === '/ended' || tabStorage.getItem('tgready-cleanup')) {
    await purgePrivateData();
    tabStorage.removeItem('tgready-cleanup');
    tabStorage.removeItem('tgready-session');
    show('网页操作已结束', '已清理本账号的网页数据。其他账号标签保持不变。', {
      label: '重新打开', run: () => location.replace('/'),
    });
    return;
  }
  show('正在核实登录', '沿用平台登录身份，准备打开所选 TG 账号。');
  const handoff = new URLSearchParams(location.hash.slice(1)).get('handoff');
  const params = new URLSearchParams(location.search);
  history.replaceState(undefined, '', handoff ? '/start' : '/');
  if (params.has('account_id') && params.has('binding_id')) {
    tabStorage.setItem(
      'tgready-launch',
      JSON.stringify({
        account_id: params.get('account_id'),
        binding_id: params.get('binding_id'),
      }),
    );
    history.replaceState(undefined, '', handoff ? '/start' : '/');
  }
  let launch: { account_id: string; binding_id: string };
  try {
    launch = JSON.parse(tabStorage.getItem('tgready-launch') || '');
  } catch {
    throw new Error('请从 TG 账号列表点击“打开网页版”进入');
  }
  if (handoff) {
    const source = await call('telegram-web/handoff-context', { ticket: handoff });
    const proof = await new Promise<{ verifier: string; sender: MessageEventSource }>((resolve, reject) => {
      const timeout = setTimeout(() => {
        window.removeEventListener('message', receive);
        reject(new Error('未收到原客户端的浏览器证明，请从账号列表重新打开'));
      }, 45000);
      function receive(event: MessageEvent) {
        if (event.origin !== source.source_origin || event.data?.type !== 'tgready-handoff'
          || event.data?.ticket !== handoff || !/^[a-f0-9]{64}$/.test(event.data?.verifier) || !event.source) return;
        clearTimeout(timeout);
        window.removeEventListener('message', receive);
        resolve({ verifier: event.data.verifier, sender: event.source });
      }
      window.addEventListener('message', receive);
    });
    const scope = await call('telegram-web/sign-in', { ticket: handoff, verifier: proof.verifier });
    if (scope.account_id !== launch.account_id || scope.binding_id !== launch.binding_id) {
      throw new Error('打开链接与交接账号不一致，请返回账号列表');
    }
    (proof.sender as WindowProxy).postMessage(
      { type: 'tgready-handoff-complete', ticket: handoff }, source.source_origin,
    );
    setTimeout(() => location.replace('/'), 100);
    return;
  }
  let me;
  try {
    me = await call('auth/me', undefined, 'GET');
  } catch (error) {
    if ((error as { status?: number }).status === 401) {
      throw new Error('请从群控客户端账号列表重新打开网页版，无需再次输入谷歌验证码', {
        cause: error,
      });
    }
    throw error;
  }
  if (me.role !== 'owner') throw new Error('请使用群控客户端账号打开网页版');
  csrf = me.csrf;
  const info = await call(
    'telegram-web/context?' + new URLSearchParams(launch).toString(),
    undefined,
    'GET',
  );
  const displayName = `${info.label || info.phone_hint}${info.username ? ` @${info.username}` : ''}`;
  handle.textContent = info.label || 'TG 账号';
  handle.title = handle.textContent;
  document.title = `${info.label || info.phone_hint} · TGReady Web`;
  let saved = JSON.parse(tabStorage.getItem('tgready-session') || '{}');
  const requestKey = `${me.subject}:${me.authenticated_at}:${launch.account_id}:${launch.binding_id}`;
  if (saved.key !== requestKey) saved = {};
  holder =
    saved.holder ||
    Array.from(crypto.getRandomValues(new Uint8Array(32)), (v) =>
      v.toString(16).padStart(2, '0'),
    ).join('');
  await bindTab(`${me.subject}:${launch.account_id}`);
  if (!saved.request_id) {
    const residential = await new Promise<boolean>((resolve) => {
      const card = show(
        '打开 Telegram 网页版',
        '将建立独立网页授权。网页操作期间，同号自动任务等待，其他账号照常执行。',
      );
      const details = document.createElement('div');
      details.className = 'tgready-row';
      details.textContent = `${info.label || info.phone_hint} · ${info.node_name}`;
      const label = document.createElement('label');
      const check = document.createElement('input');
      check.type = 'checkbox';
      label.append(check, ' 使用家宽');
      const note = document.createElement('p');
      note.textContent = '聊天与文件通过家宽，仅计量、不扣费；通话和第三方小程序使用浏览器网络。';
      note.hidden = true;
      check.onchange = () => {
        note.hidden = !check.checked;
      };
      const button = document.createElement('button');
      button.className = 'tgready-button';
      button.textContent = '开始打开';
      button.onclick = () => {
        button.disabled = true;
        resolve(check.checked);
      };
      card.append(details, label, note, document.createElement('br'), button);
    });
    saved = { key: requestKey, request_id: crypto.randomUUID(), holder, residential };
    tabStorage.setItem('tgready-session', JSON.stringify(saved));
  }
  let row = await call('telegram-web/sessions', {
    ...launch,
    request_id: saved.request_id,
    holder,
    residential: saved.residential,
  });
  sessionId = row.id;
  applyStatus(row);
  usesResidential = row.residential;
  usageText.textContent = usesResidential ? '家宽用量同步中' : '家宽未启用';
  endButton.disabled = false;
  void heartbeat();
  while (row.state === 'waiting_account') {
    applyStatus(row);
    await sleep(2000);
    row = await call(`telegram-web/sessions/${sessionId}/heartbeat`);
  }
  if (!['active', 'authorizing', 'waiting_verification', 'reconnecting'].includes(row.state)) {
    applyStatus(row);
    return;
  }
  show('正在建立独立网页授权', '保留原节点登录，完成身份核对后进入聊天。');
  const boot = await call(`telegram-web/sessions/${sessionId}/bootstrap`);
  const context: WebContext = {
    sessionId,
    generation: row.generation,
    holder,
    csrf,
    namespace: sessionId,
    apiId: boot.api_id,
    wasActive: row.state === 'active',
    apiHash: boot.api_hash,
    deviceModel: row.device_model,
    expectedUserId: row.expected_user_id,
    accountTitle: displayName,
  };
  (globalThis as typeof globalThis & { tgready?: WebContext }).tgready = context;
  if (localStorage.getItem('tgready-web-session') !== sessionId) {
    await purgePrivateData();
    localStorage.setItem('tgready-web-session', sessionId);
  }
  installSandbox();
  panel.hidden = true;
  return context;
}

export function showFailure(error: unknown) {
  stopSandbox();
  show('暂时无法打开网页版', error instanceof Error ? error.message : '连接暂不可用，请重试', {
    label: '重试',
    run: () => location.reload(),
  });
}
